/**
 * Client-side recomposition of the equipment-tab condition rollup.
 *
 * Mirrors the legacy backend logic in
 * internal/handlers/equipmenttab/service.go (mapEquipmentCondition +
 * buildTradeBuckets). Spec 2026-05-05-03 deletes that handler and moves
 * this logic to the client.
 */

export type EquipmentCondition =
  | 'new'
  | 'good'
  | 'fair'
  | 'poor'
  | 'end_of_life'
  | 'unassessed'

/** Per-trade rollup, mirroring the API's `condition_rollup` shape. */
export interface ConditionRollup {
  poor: number
  end_of_life: number
  unassessed: number
  total: number
}

/** A single equipment row, projected to what the rollup needs. */
export interface EquipmentForRollup {
  /** UID of the trade asset type (the equipment's `inherits_from_asset_type_uid`). */
  tradeUid: string | undefined | null
  /** The current value of the `equipment_condition` property (1..5 or null). */
  conditionRaw?: unknown
}

/**
 * Map a raw `equipment_condition` value (1..5, or null/missing) onto the
 * canonical EquipmentCondition string. Missing or unparseable values map to
 * `unassessed` so they can still be counted.
 */
export function mapEquipmentCondition(raw: unknown): EquipmentCondition {
  if (raw === null || raw === undefined) return 'unassessed'
  const s = String(raw)
  switch (s) {
    case '1':
      return 'new'
    case '2':
      return 'good'
    case '3':
      return 'fair'
    case '4':
      return 'poor'
    case '5':
      return 'end_of_life'
    default:
      return 'unassessed'
  }
}

/**
 * Compute the per-trade rollup. Each entry in `equipment` is bucketed by
 * its `tradeUid`; equipment without a trade UID (i.e. parented under a
 * non-trade type) is dropped — Zone B only renders rows under trades.
 */
export function computeConditionRollup(
  equipment: ReadonlyArray<EquipmentForRollup>,
): Map<string, ConditionRollup> {
  const out = new Map<string, ConditionRollup>()
  for (const item of equipment) {
    if (!item.tradeUid) continue
    let bucket = out.get(item.tradeUid)
    if (!bucket) {
      bucket = { poor: 0, end_of_life: 0, unassessed: 0, total: 0 }
      out.set(item.tradeUid, bucket)
    }
    bucket.total += 1
    const c = mapEquipmentCondition(item.conditionRaw)
    if (c === 'poor') bucket.poor += 1
    else if (c === 'end_of_life') bucket.end_of_life += 1
    else if (c === 'unassessed') bucket.unassessed += 1
  }
  return out
}
